import { Button, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, SelectChangeEvent, Stack, TextField } from "@mui/material";
import * as React from "react";
import { ReactNode } from "react";
import { NumericField } from "../../../controls";
import { ApplicationTooltip } from "../../../views";
import { numberFormatters } from "../../../../shared/utils/numberFormatters";
import { AutoOrderState } from "../../../../../screens/portfolio-info/types/AutoOrderState";
import { AutoOrderStrategy } from "../../../../../screens/portfolio-info/types/AutoOrderStrategy";

export const AUTO_ORDER_STRATEGY_VALUE = AutoOrderStrategy.MarketOptionAskOrderHedgedWithFeature;
export const AUTO_ORDER_STRATEGY_TITLE = "Stop call hedged with sold feature";
export const AUTO_ORDER_STRATEGY_TOOLTIP = "Take a market option ask order and hedge it with a feature";

export type AutoOrderParametersState = {
    strategy: AutoOrderStrategy;
    state: AutoOrderState;
    stateDescription: string | null;
    isActive: boolean;
    targetPrice: number;
    maxIvLevel: number;
    maxPriceDelta: number;
    netDelta: number;
};

type AutoOrderParametersProps = {
    optionOrderSymbol: string;
    optionOrderSize: number;
    parameters: AutoOrderParametersState;
    handleParametersChange: (patch: Partial<AutoOrderParametersState>) => void;
    onResetOrderState: () => void;
    isResetOrderStateDisabled: boolean;
};

export function AutoOrderParameters({
    optionOrderSymbol,
    optionOrderSize,
    parameters,
    handleParametersChange,
    onResetOrderState,
    isResetOrderStateDisabled,
}: AutoOrderParametersProps) {
    const handleStrategyChange = (event: SelectChangeEvent<string>): void => {
        handleParametersChange({ strategy: Number(event.target.value) as AutoOrderStrategy });
    };

    return (
        <Stack direction="column" spacing={1.7}>
            <Stack direction="row" spacing={1}>
                <ApplicationTooltip
                    title="Activates this auto order setup"
                    placement="top-start"
                    disableInteractive
                    offset={[0, -8]}
                >
                    <FormControlLabel
                        control={
                            <Checkbox
                                sx={{
                                    padding: 0,
                                    marginRight: 0.5,
                                    marginLeft: "-3px",
                                    "&.Mui-disabled": {
                                        color: "rgba(0, 0, 0, 0.25)",
                                    },
                                }}
                                color="default"
                                id="autoOrderIsActive"
                                name="autoOrderIsActive"
                                checked={parameters.isActive}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                    handleParametersChange({ isActive: event.target.checked })}
                            />
                        }
                        label="Enable"
                        sx={{
                            padding: 0,
                            margin: 0,
                        }}
                    />
                </ApplicationTooltip>
            </Stack>
            <FormControl
                variant="standard"
                size="small"
                sx={{ width: 470 }}
            >
                <InputLabel id="auto-order-strategy-label">
                    Strategy
                </InputLabel>
                <Select
                    labelId="auto-order-strategy-label"
                    id="autoOrderStrategy"
                    name="autoOrderStrategy"
                    value={String(parameters.strategy)}
                    onChange={handleStrategyChange}
                >
                    <MenuItem value={String(AUTO_ORDER_STRATEGY_VALUE)}>
                        {AUTO_ORDER_STRATEGY_TITLE}
                    </MenuItem>
                </Select>
            </FormControl>
            <Stack direction="row" spacing={2}>
                <TextField
                    margin="normal"
                    id="optionOrderSymbol"
                    name="optionOrderSymbol"
                    label="Option contract"
                    type="string"
                    size="small"
                    value={optionOrderSymbol}
                    variant="standard"
                    disabled
                    sx={{
                        width: 175,
                    }}
                />
                <TextField
                    margin="normal"
                    id="optionOrderSize"
                    name="optionOrderSize"
                    label="Size"
                    type="string"
                    size="small"
                    value={optionOrderSize}
                    variant="standard"
                    disabled
                    sx={{
                        width: 50
                    }}
                />
                {withTooltip(
                    "Underlying price where the option contract must be filled",
                    <NumericField
                        id="targetPrice"
                        name="targetPrice"
                        label="Target price"
                        value={parameters.targetPrice}
                        onValueChange={(value: number) => handleParametersChange({ targetPrice: value })}
                        formatter={(value: number) => numberFormatters.format0(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                        width={80}
                        step={100}
                    />
                )}
            </Stack>
            <Stack direction="row" spacing={2}>
                {withTooltip(
                    "Maximum acceptable IV value for the order",
                    <NumericField
                        id="maxIvLevel"
                        name="maxIvLevel"
                        label="Max IV level"
                        value={parameters.maxIvLevel}
                        onValueChange={(value: number) => handleParametersChange({ maxIvLevel: value })}
                        formatter={(value: number) => numberFormatters.format4(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                        width={80}
                        step={0.01}
                    />
                )}
                {withTooltip(
                    "Maximum delta between the target option theoretical price level and the best ask price in the order book for market order execution",
                    <NumericField
                        id="maxPriceDelta"
                        name="maxPriceDelta"
                        label="Max price delta"
                        value={parameters.maxPriceDelta}
                        onValueChange={(value: number) => handleParametersChange({ maxPriceDelta: value })}
                        formatter={(value: number) => numberFormatters.format0(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                        width={90}
                        step={1}
                    />
                )}
                {withTooltip(
                    "Target net delta after hedging",
                    <NumericField
                        id="netDelta"
                        name="netDelta"
                        label="Net delta"
                        value={parameters.netDelta}
                        onValueChange={(value: number) => handleParametersChange({ netDelta: value })}
                        formatter={(value: number) => numberFormatters.format2(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                        width={80}
                        step={0.01}
                    />
                )}
            </Stack>
            <Stack direction="row" spacing={2}>
                <TextField
                    margin="normal"
                    id="autoOrderState"
                    name="autoOrderState"
                    label="State"
                    type="string"
                    size="small"
                    value={formatAutoOrderState(parameters.state)}
                    variant="standard"
                    disabled
                    sx={{
                        width: 120,
                    }}
                />
                <TextField
                    margin="normal"
                    id="autoOrderStateDescription"
                    name="autoOrderStateDescription"
                    label="State description"
                    type="string"
                    size="small"
                    value={parameters.stateDescription ?? ' '}
                    variant="standard"
                    disabled
                    sx={{
                        width: 330,
                    }}
                />
            </Stack>
            <Stack direction="row">
                <Button
                    size="small"
                    variant="contained"
                    color="error"
                    disabled={isResetOrderStateDisabled}
                    onClick={onResetOrderState}
                >
                    Reset order state
                </Button>
            </Stack>
        </Stack>
    );
}

function formatAutoOrderState(state: AutoOrderState): string {
    return AutoOrderState[state] ?? String(state);
}

function withTooltip(title: string, children: ReactNode): ReactNode {
    return (
        <ApplicationTooltip
            title={title}
            placement="top-start"
            disableInteractive
            offset={[0, -8]}
        >
            <span>
                {children}
            </span>
        </ApplicationTooltip>
    );
}
